import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '../services/api';
import { ActivityCategoryVersion } from '../types/api';

interface SystemSettings {
  tokenRequirement: number;
  membershipFeeAmount: number;
  activityApprovalRequired: boolean;
  emailNotifications: boolean;
  automaticTokenCalculation: boolean;
  trackerAssignmentRequired: boolean;
  compulsoryActivities: string[];
  organizationName: string;
  systemAdminEmail: string;
  backupFrequency: string;
}

// Using ActivityCategoryVersion from types/api.ts instead

export const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();

  // Mock settings for now - will need backend endpoint later
  const settings: SystemSettings = useMemo(() => ({
    tokenRequirement: 1500,
    membershipFeeAmount: 500,
    activityApprovalRequired: true,
    emailNotifications: true,
    automaticTokenCalculation: true,
    trackerAssignmentRequired: true,
    compulsoryActivities: [],
    organizationName: 'Sustainable Agriculture Network',
    systemAdminEmail: 'admin@san.org',
    backupFrequency: 'daily'
  }), []);

  const { data: categories } = useQuery({
    queryKey: ['categories-2025'], 
    queryFn: () => categoriesApi.getVersionsByYear(2025)
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: Partial<SystemSettings>) => {
      // Mock settings update - will need backend endpoint later
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      return newSettings;
    },
    onSuccess: () => {
      setIsEditing(false);
      // Settings are mocked, so no need to invalidate queries
    }
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ versionId, updates }: { versionId: number; updates: Partial<ActivityCategoryVersion> }) => 
      categoriesApi.updateVersion(versionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-2025'] });
    }
  });

  const [formData, setFormData] = useState<SystemSettings>({
    tokenRequirement: 1500,
    membershipFeeAmount: 500,
    activityApprovalRequired: true,
    emailNotifications: true,
    automaticTokenCalculation: true,
    trackerAssignmentRequired: true,
    compulsoryActivities: [],
    organizationName: 'Sustainable Agriculture Network',
    systemAdminEmail: 'it@san.eco',
    backupFrequency: 'daily'
  });

  React.useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettingsMutation.mutate(formData);
  };

  const handleCategoryUpdate = (versionId: number, field: string, value: any) => {
    updateCategoryMutation.mutate({
      versionId,
      updates: { [field]: value }
    });
  };

  // No loading state needed since settings are mocked for now

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                disabled={updateSettingsMutation.isPending}
              >
                {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Edit Settings
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            General Settings
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activity Categories
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'backup'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Backup & Security
          </button>
        </nav>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">General Configuration</h3>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Organization Name</label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">System Admin Email</label>
                <input
                  type="email"
                  value={formData.systemAdminEmail}
                  onChange={(e) => setFormData({ ...formData, systemAdminEmail: e.target.value })}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Token Requirement</label>
                <input
                  type="number"
                  value={formData.tokenRequirement}
                  onChange={(e) => setFormData({ ...formData, tokenRequirement: parseInt(e.target.value) })}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Membership Fee Amount ($)</label>
                <input
                  type="number"
                  value={formData.membershipFeeAmount}
                  onChange={(e) => setFormData({ ...formData, membershipFeeAmount: parseInt(e.target.value) })}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">System Behavior</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">Activity Approval Required</h5>
                    <p className="text-sm text-gray-500">Require admin approval for all activity submissions</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.activityApprovalRequired}
                    onChange={(e) => setFormData({ ...formData, activityApprovalRequired: e.target.checked })}
                    disabled={!isEditing}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">Automatic Token Calculation</h5>
                    <p className="text-sm text-gray-500">Automatically calculate token values from activity categories</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.automaticTokenCalculation}
                    onChange={(e) => setFormData({ ...formData, automaticTokenCalculation: e.target.checked })}
                    disabled={!isEditing}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">Tracker Assignment Required</h5>
                    <p className="text-sm text-gray-500">Require tracker assignment for all activities</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.trackerAssignmentRequired}
                    onChange={(e) => setFormData({ ...formData, trackerAssignmentRequired: e.target.checked })}
                    disabled={!isEditing}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Activity Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Activity Categories Configuration</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Tracker</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compulsory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories?.map((categoryVersion) => (
                  <tr key={categoryVersion.version_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Category {categoryVersion.category_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        value={categoryVersion.token_value}
                        onChange={(e) => handleCategoryUpdate(categoryVersion.version_id, 'token_value', parseInt(e.target.value))}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={categoryVersion.tracker_role || ''}
                        onChange={(e) => handleCategoryUpdate(categoryVersion.version_id, 'tracker_role', e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Unassigned</option>
                        <option value="Ana G.">Ana G.</option>
                        <option value="Jasson M.">Jasson M.</option>
                        <option value="Catalina M.">Catalina M.</option>
                        <option value="Eliane A.">Eliane A.</option>
                        <option value="IT">IT</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={categoryVersion.must_do}
                        onChange={(e) => handleCategoryUpdate(categoryVersion.version_id, 'must_do', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {categoryVersion.max_tokens_per_cycle ? `Max ${categoryVersion.max_tokens_per_cycle} tokens` : 'No limit'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
                <p className="text-sm text-gray-500">Send email notifications for system events</p>
              </div>
              <input
                type="checkbox"
                checked={formData.emailNotifications}
                onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                disabled={!isEditing}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Notification Events</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">New activity submissions</span>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Payment recordings</span>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Member token milestones</span>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">System maintenance alerts</span>
                  <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup & Security Tab */}
      {activeTab === 'backup' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Backup & Security Settings</h3>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Backup Frequency</label>
              <select
                value={formData.backupFrequency}
                onChange={(e) => setFormData({ ...formData, backupFrequency: e.target.value })}
                disabled={!isEditing}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Security Features</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Two-factor authentication required</span>
                  <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Audit log retention (90 days)</span>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Data encryption at rest</span>
                  <input type="checkbox" defaultChecked className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-4">Data Management</h4>
              <div className="space-y-3">
                <button className="w-full text-left px-4 py-2 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100">
                  <div className="text-sm font-medium text-blue-900">Export System Data</div>
                  <div className="text-xs text-blue-700">Download complete database backup</div>
                </button>
                <button className="w-full text-left px-4 py-2 bg-green-50 border border-green-200 rounded-md hover:bg-green-100">
                  <div className="text-sm font-medium text-green-900">Generate Audit Report</div>
                  <div className="text-xs text-green-700">Create comprehensive activity audit report</div>
                </button>
                <button className="w-full text-left px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-md hover:bg-yellow-100">
                  <div className="text-sm font-medium text-yellow-900">System Health Check</div>
                  <div className="text-xs text-yellow-700">Run complete system diagnostics</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};